
export const MAIA_PLUGIN_SERVER = "https://urlparser.maia.id";

export interface PluginSettings {
  pluginServer: string;
  queryKey?: string;
  accessKey?: string;
  maxChars?: number;
}

export interface SearchParams {
  query: string;
  language?: string;
  time_range?: string;
  safesearch?: number;
  categories?: string;
  limit?: number;
}

function safeTruncate(text: string, maxChars: number): string {
  if (!text || typeof text !== 'string') return '';
  if (!maxChars || Number.isNaN(maxChars)) return text;
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  // try to end on a word boundary
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > maxChars * 0.7 ? slice.slice(0, lastSpace) : slice) + '...';
}

async function fetchPageContent(url: string, pluginServer: string, queryKey?: string, accessKey?: string): Promise<string> {
  const response = await fetch(
    `${pluginServer}/?${queryKey || 'url'}=${encodeURIComponent(url)}`
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch web content: ${response.status} - ${response.statusText}`
    );
  }

  const data = await response.json();
  const key = accessKey || 'content';
  const raw = typeof data === 'object' && data !== null ? (data[key] ?? data) : data;
  // If server returned an object, stringify for downstream readability
  return typeof raw === 'string' ? raw : JSON.stringify(raw);
}

export async function read_web_page_content(params: { url: string }, userSettings: PluginSettings): Promise<string> {
  const { url } = params;
  const { pluginServer, queryKey, accessKey, maxChars } = userSettings;

  if (!pluginServer) {
    throw new Error(
      'Missing plugin server URL. Please set it in the plugin settings.'
    );
  }

  try {
    const cleanPluginServer = pluginServer.endsWith('/') ? pluginServer.slice(0, -1) : pluginServer;
    const content = await fetchPageContent(url, cleanPluginServer, queryKey, accessKey);
    const limit = maxChars ? Number(maxChars) : 8000;
    return safeTruncate(content, Number.isFinite(limit) ? limit : 8000);
  } catch (error: any) {
    console.error('Error summarizing webpage:', error);
    // Surface a concise, actionable error to the model
    return `Error: Unable to fetch the page content (${error?.message || 'unknown error'}).`;
  }
}

export async function get_maia_search_results(params: SearchParams): Promise<string> {
  const query = params.query || '';
  const language = params.language || 'id';
  const time_range = params.time_range || 'day';
  const safesearch = params.safesearch || 0;
  const categories = params.categories || 'general';
  const limit = params.limit || 20;

  console.log('Search parameters:', {
    query,
    language,
    time_range,
    safesearch,
    categories,
    limit
  });

  if (!query) {
    console.error('Search query is missing');
    return "Error: Search query is missing";
  }

  // Use local proxy /api/maia which redirects to https://search.maia.id to avoid CORS
  const url = `/api/maia/search?q=${encodeURIComponent(query)}&language=${encodeURIComponent(language)}&time_range=${encodeURIComponent(time_range)}&safesearch=${safesearch}&categories=${encodeURIComponent(categories)}&limit=${limit}&format=json`;

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
       throw new Error(`Search API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Response data:', data);

    if (data.error) {
      console.error('API error:', data.error);
      throw new Error('Error: ' + data.error.message);
    }

    const results = data.results || [];
    if (results.length === 0) return "Tidak ada hasil pencarian yang ditemukan.";

    return results
      .map(
        (item: any) => {
          return `
Title: ${item.title}
Content: ${item.content}
URL: ${item.url}
 `;
        }
      )
      .join('\n');
  } catch (error) {
    console.error('Fetch error:', error);
    return `Error searching for news: ${error}`;
  }
}
